import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { TenantProvider } from "@/hooks/useTenant";
import { CartProvider } from "@/hooks/useCart";
import { FavoritesProvider } from "@/hooks/useFavorites";
import { StoreSettingsProvider } from "@/hooks/useStoreSettings";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminLayout from "@/components/admin/AdminLayout";
import StoreLayout from "@/components/store/StoreLayout";

/* ── Lazy-loaded pages ──────────────────────── */

const Auth = lazy(() => import("@/pages/Auth"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const RegisterStore = lazy(() => import("@/pages/RegisterStore"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Landing = lazy(() => import("@/pages/Landing"));

// Admin
const Dashboard = lazy(() => import("@/pages/admin/Dashboard"));
const Products = lazy(() => import("@/pages/admin/Products"));
const ProductVisualEditor = lazy(() => import("@/pages/admin/ProductVisualEditor"));
const Categories = lazy(() => import("@/pages/admin/Categories"));
const Brands = lazy(() => import("@/pages/admin/Brands"));
const Orders = lazy(() => import("@/pages/admin/Orders"));
const Delivery = lazy(() => import("@/pages/admin/Delivery"));
const Schedules = lazy(() => import("@/pages/admin/Schedules"));
const UsersAdmin = lazy(() => import("@/pages/admin/UsersAdmin"));
const StoreSettingsPage = lazy(() => import("@/pages/admin/StoreSettings"));
const StockMovements = lazy(() => import("@/pages/admin/StockMovements"));
const StockReports = lazy(() => import("@/pages/admin/StockReports"));
const InventoryCount = lazy(() => import("@/pages/admin/InventoryCount"));
const Banners = lazy(() => import("@/pages/admin/Banners"));
const Reviews = lazy(() => import("@/pages/admin/Reviews"));
const Coupons = lazy(() => import("@/pages/admin/Coupons"));
const Financial = lazy(() => import("@/pages/admin/Financial"));

// Store
const Home = lazy(() => import("@/pages/store/Home"));
const Catalog = lazy(() => import("@/pages/store/Catalog"));
const Profile = lazy(() => import("@/pages/store/Profile"));
const ProductDetail = lazy(() => import("@/pages/store/ProductDetail"));
const Cart = lazy(() => import("@/pages/store/Cart"));
const Checkout = lazy(() => import("@/pages/store/Checkout"));
const OrderConfirmation = lazy(() => import("@/pages/store/OrderConfirmation"));
const MyOrders = lazy(() => import("@/pages/store/MyOrders"));
const Favorites = lazy(() => import("@/pages/store/Favorites"));
const Account = lazy(() => import("@/pages/store/Account"));
const About = lazy(() => import("@/pages/store/About"));
const Privacy = lazy(() => import("@/pages/store/Privacy"));
const Exchanges = lazy(() => import("@/pages/store/Exchanges"));
const PaymentInfo = lazy(() => import("@/pages/store/Payment"));
const Contact = lazy(() => import("@/pages/store/Contact"));

/* ── QueryClient with smart defaults ────────── */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 2,
      gcTime: 1000 * 60 * 10,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/* ── Route-level loading fallback ───────────── */

function PageLoader() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    </div>
  );
}

/* ── Protected store routes (reusable for root + /loja/:slug) ── */

function protectedStoreRoutes(prefix: string) {
  return (
    <>
      <Route path={`${prefix}/favorites`} element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
      <Route path={`${prefix}/checkout`} element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
      <Route path={`${prefix}/order-confirmation/:orderNumber`} element={<ProtectedRoute><OrderConfirmation /></ProtectedRoute>} />
      <Route path={`${prefix}/orders`} element={<ProtectedRoute><MyOrders /></ProtectedRoute>} />
      <Route path={`${prefix}/profile`} element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path={`${prefix}/account`} element={<ProtectedRoute><Account /></ProtectedRoute>} />
    </>
  );
}

function storeLayoutRoutes(prefix: string) {
  return (
    <Route element={<StoreLayout />}>
      <Route index element={<Home />} />
      <Route path="catalog" element={<Catalog />} />
      <Route path="product/:id" element={<ProductDetail />} />
      <Route path="cart" element={<Cart />} />
      <Route path="favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
      <Route path="checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
      <Route path="order-confirmation/:orderNumber" element={<ProtectedRoute><OrderConfirmation /></ProtectedRoute>} />
      <Route path="orders" element={<ProtectedRoute><MyOrders /></ProtectedRoute>} />
      <Route path="profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
      <Route path="about" element={<About />} />
      <Route path="privacy" element={<Privacy />} />
      <Route path="exchanges" element={<Exchanges />} />
      <Route path="payment-info" element={<PaymentInfo />} />
      <Route path="contact" element={<Contact />} />
    </Route>
  );
}

function AppRoutes() {
  const { user, loading, isAdmin, isEmployee } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // Admin users go straight to admin panel
  if (user && (isAdmin || isEmployee)) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/register-store" element={<Navigate to="/admin" replace />} />
          <Route
            path="/admin/products/:id/edit"
            element={
              <ProtectedRoute requiredRoles={["admin", "employee"]}>
                <ProductVisualEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRoles={["admin", "employee"]}>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="products" element={<Products />} />
            <Route path="categories" element={<Categories />} />
            <Route path="brands" element={<Brands />} />
            <Route path="orders" element={<Orders />} />
            <Route path="delivery" element={<Delivery />} />
            <Route path="schedules" element={<Schedules />} />
            <Route path="users" element={<UsersAdmin />} />
            <Route path="settings" element={<StoreSettingsPage />} />
            <Route path="stock-movements" element={<StockMovements />} />
            <Route path="stock-reports" element={<StockReports />} />
            <Route path="inventory-count" element={<InventoryCount />} />
            <Route path="banners" element={<Banners />} />
            <Route path="reviews" element={<Reviews />} />
            <Route path="coupons" element={<Coupons />} />
            <Route path="financial" element={<Financial />} />
          </Route>

          {/* Tenant-scoped store */}
          <Route path="/loja/:slug/auth" element={<Navigate to="/admin" replace />} />
          <Route path="/loja/:slug/*" element={<StoreLayout />}>
            <Route index element={<Home />} />
            <Route path="catalog" element={<Catalog />} />
            <Route path="product/:id" element={<ProductDetail />} />
            <Route path="cart" element={<Cart />} />
            <Route path="favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
            <Route path="checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
            <Route path="order-confirmation/:orderNumber" element={<ProtectedRoute><OrderConfirmation /></ProtectedRoute>} />
            <Route path="orders" element={<ProtectedRoute><MyOrders /></ProtectedRoute>} />
            <Route path="profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
            <Route path="about" element={<About />} />
            <Route path="privacy" element={<Privacy />} />
            <Route path="exchanges" element={<Exchanges />} />
            <Route path="payment-info" element={<PaymentInfo />} />
            <Route path="contact" element={<Contact />} />
          </Route>

          {/* Root store (admin preview) */}
          <Route element={<StoreLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/catalog" element={<Catalog />} />
            <Route path="/product/:id" element={<ProductDetail />} />
            <Route path="/cart" element={<Cart />} />
            <Route path="/favorites" element={<Favorites />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/order-confirmation/:orderNumber" element={<OrderConfirmation />} />
            <Route path="/orders" element={<MyOrders />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/account" element={<Account />} />
            <Route path="/about" element={<About />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/exchanges" element={<Exchanges />} />
            <Route path="/payment-info" element={<PaymentInfo />} />
            <Route path="/contact" element={<Contact />} />
          </Route>

          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/forgot-password" element={user ? <Navigate to="/" replace /> : <ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/register-store" element={user ? <Navigate to="/" replace /> : <RegisterStore />} />

        {/* Tenant-scoped store */}
        <Route path="/loja/:slug/auth" element={user ? <Navigate to="/" replace /> : <Auth />} />
        <Route path="/loja/:slug/*" element={<StoreLayout />}>
          <Route index element={<Home />} />
          <Route path="catalog" element={<Catalog />} />
          <Route path="product/:id" element={<ProductDetail />} />
          <Route path="cart" element={<Cart />} />
          <Route path="favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
          <Route path="checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
          <Route path="order-confirmation/:orderNumber" element={<ProtectedRoute><OrderConfirmation /></ProtectedRoute>} />
          <Route path="orders" element={<ProtectedRoute><MyOrders /></ProtectedRoute>} />
          <Route path="profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
          <Route path="about" element={<About />} />
          <Route path="privacy" element={<Privacy />} />
          <Route path="exchanges" element={<Exchanges />} />
          <Route path="payment-info" element={<PaymentInfo />} />
          <Route path="contact" element={<Contact />} />
        </Route>

        {/* Root routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/auth" element={user ? <Navigate to="/" replace /> : <Auth />} />
        <Route element={<StoreLayout />}>
          <Route path="/catalog" element={<Catalog />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/favorites" element={<ProtectedRoute><Favorites /></ProtectedRoute>} />
          <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
          <Route path="/order-confirmation/:orderNumber" element={<ProtectedRoute><OrderConfirmation /></ProtectedRoute>} />
          <Route path="/orders" element={<ProtectedRoute><MyOrders /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
          <Route path="/about" element={<About />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/exchanges" element={<Exchanges />} />
          <Route path="/payment-info" element={<PaymentInfo />} />
          <Route path="/contact" element={<Contact />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <BrowserRouter>
        <AuthProvider>
          <TenantProvider>
            <StoreSettingsProvider>
              <CartProvider>
                <FavoritesProvider>
                  <AppRoutes />
                </FavoritesProvider>
              </CartProvider>
            </StoreSettingsProvider>
          </TenantProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
